import { useCallback, useState } from 'react';
import { apiClient } from '../lib/api/client';
import { ApiResponse, GetQbTestForEditOutput, QBTestQuestionEto, QbTestCoursesGroupEto } from '../lib/api/types';
import { WhiteboardExternalProps } from '../types/whiteboardProps';

interface UseTestLoaderProps {
  accessToken: string | null;
  tenantId: number | null;
  externalProps?: WhiteboardExternalProps;
}

interface UseTestLoaderReturn {
  loadTest: (testId: string) => Promise<GetQbTestForEditOutput | null>;
  loadQuestions: (testId: string) => Promise<{
    questions: QBTestQuestionEto[];
    groups: QbTestCoursesGroupEto[];
  }>;
  isLoading: boolean;
}

export function useTestLoader({
  accessToken,
  tenantId,
  externalProps
}: UseTestLoaderProps): UseTestLoaderReturn {
  const [isLoading, setIsLoading] = useState(false);
  
  const loadTest = useCallback(async (testId: string): Promise<GetQbTestForEditOutput | null> => {
    if (!testId) return null;
    
    try {
      setIsLoading(true);
      
      // If external props, use external data service
      if (externalProps) {
        // For external mode, we might not need to load test data separately
        return null;
      }
      
      // Otherwise use API client - use Id parameter (not testId)
      const response = await apiClient.get<ApiResponse<GetQbTestForEditOutput>>(
        `/api/services/app/QbTests/GetQbTestForEdit`,
        {
          params: {
            Id: testId  // Use Id parameter, not testId
          },
          headers: {
            'Abp.TenantId': tenantId
          }
        }
      );
      
      if (response.data?.success && response.data?.result) {
        return response.data.result;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to load test:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, externalProps]);
  
  const loadQuestions = useCallback(async (testId: string): Promise<{
    questions: QBTestQuestionEto[];
    groups: QbTestCoursesGroupEto[];
  }> => {
    if (!testId) return { questions: [], groups: [] };
    
    try {
      setIsLoading(true);
      
      // If external props, use provided data
      if (externalProps?.questions) {
        // Map Question[] to QBTestQuestionEto[]
        const mappedQuestions: QBTestQuestionEto[] = externalProps.questions.map((q, index) => ({
          questionId: q.id,
          order: index + 1,
          testId: testId,
          courseId: 0, // Default value
          subjectId: 0, // Default value
          groupId: 0, // Default value
          isCritical: false,
          isAssigned: true,
          isDifferentiated: false,
          questionLevel: 'medium', // Default value
          // Add any other required fields with defaults
        }));
        
        return {
          questions: mappedQuestions,
          groups: []
        };
      }
      
      // Otherwise load from API
      const response = await apiClient.get<ApiResponse<QbTestCoursesGroupEto[]>>(
        `/api/services/app/QbTestQuestions/GetQbQuestionsByTestId`,
        {
          params: {
            testId: testId
          },
          headers: {
            'Abp.TenantId': tenantId
          }
        }
      );
      
      if (response.data?.success && response.data?.result) {
        // Result is an array of groups
        const groups = response.data.result;
        
        // Transform nested structure to flat question list while preserving order
        const allQuestions: QBTestQuestionEto[] = [];
        
        // Process groups in order
        groups
          .sort((a, b) => (a.groupOrder || 0) - (b.groupOrder || 0))
          .forEach(group => {
            // Process courses in order within each group
            (group.courses ?? [])
              .sort((a, b) => (a.courseOrder || 0) - (b.courseOrder || 0))
              .forEach(course => {
                // Add questions in order within each course
                (course.questions ?? [])
                  .filter(q => !q.isCommonText)
                  .sort((a, b) => {
                    // First try questionNumber, then questionOrder
                    if (a.questionNumber !== undefined && b.questionNumber !== undefined) {
                      return a.questionNumber - b.questionNumber;
                    }
                    return (a.questionOrder || 0) - (b.questionOrder || 0);
                  })
                  .forEach(question => {
                    allQuestions.push(question);
                  });
              });
          });
        
        // Check if all questions have questionNumber field
        const allHaveQuestionNumber = allQuestions.every(q => q.questionNumber !== undefined);
        
        // If all questions have questionNumber, sort by that globally
        const filteredQuestions = allHaveQuestionNumber
          ? allQuestions.sort((a, b) => (a.questionNumber || 0) - (b.questionNumber || 0))
          : allQuestions;
        
      
        
        return {
          questions: filteredQuestions,
          groups: groups
        };
      }
      
      return { questions: [], groups: [] };
    } catch (error) {
      console.error('Failed to load questions:', error);
      return { questions: [], groups: [] };
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, externalProps]);
  
  return {
    loadTest,
    loadQuestions,
    isLoading
  };
}